

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ShareIcon from "@mui/icons-material/Share";
import { useEffect, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const InstagramProductSelect = () => {
  const navigate = useNavigate();
  const influencerId = localStorage.getItem("influencerID");

  const [token, setToken] = useState("");
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [shareableLink, setShareableLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);
  const [createError, setCreateError] = useState("");

  useEffect(() => {
    const fetchTokenAndPosts = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://server-side-influencer.onrender.com/influencer/get-token/${influencerId}`
        );
        const data = await res.json();

        if (data.access_token) {
          setToken(data.access_token);

          const postRes = await fetch(
            `https://graph.instagram.com/me/media?fields=id,media_type&access_token=${data.access_token}`
          );
          const postData = await postRes.json();

          const detailedPosts = await Promise.all(
            (postData.data || []).map(async (item) => {
              const detailRes = await fetch(
                `https://graph.instagram.com/${item.id}?fields=id,media_type,media_url,caption&access_token=${data.access_token}`
              );
              return await detailRes.json();
            })
          );

          setPosts(detailedPosts);
          setError("");
        } else {
          setError("Access token not found in response.");
        }
      } catch (err) {
        console.error("Error fetching token or posts:", err);
        setError("Failed to fetch posts.");
      } finally {
        setLoading(false);
      }
    };

    if (influencerId) {
      fetchTokenAndPosts();
    }
  }, [influencerId]);

  const handleSelect = (post) => {
    setSelectedPost(post);
    setDropdownOpen(false);
  };

  const handleCreateProductLink = async () => {
    setCreatingLink(true);
    setCreateError("");
    setShareableLink("");

    if (!productUrl.trim()) {
      setCreateError("Please enter a product URL.");
      setCreatingLink(false);
      return;
    }

    try {
      const res = await fetch(
        `https://server-side-influencer.onrender.com/influencer/createproduct/${influencerId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: productUrl,
            slug: customTitle.trim() || undefined,
            image: selectedPost?.media_url,
            caption: selectedPost?.caption,
            customTitle: customTitle.trim(),
          }),
        }
      );

      const data = await res.json();

      if (res.ok) {
        setShareableLink(data.shareableLink);
        setCreateError("");
      } else {
        setCreateError(data.error || "Failed to create product link");
      }
    } catch (err) {
      console.error("Error creating product link:", err);
      setCreateError("Error creating product link");
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (shareableLink) {
      navigator.clipboard.writeText(shareableLink);
      alert("Link copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20 px-4 pb-20 relative">
      {/* Fixed Header */}
      <div className="bg-gray-100 text-black flex items-center justify-between p-4 fixed w-full top-0 left-0 z-50 shadow">
        <div onClick={() => navigate(-1)} className="cursor-pointer flex items-center">
          <IoArrowBack size={24} />
          <span className="ml-2">Back</span>
        </div>
        <div className="w-24" />
      </div>

      {/* My Post Button - Top Right */}
      <div className="flex justify-end w-full mt-4 mb-6">
        <button
          onClick={() => navigate("/myproductpost")}
          className="text-sm px-4 py-2 bg-gradient-to-br from-purple-600 to-blue-500 text-white rounded-md hover:bg-gradient-to-bl transition-all shadow-md"
        >
          My Post
        </button>
      </div>

      {/* Token Errorsssssss  vvvv*/}
      {!loading && !token && (
        <p className="text-center text-red-500 mt-10">
          Please connect your Instagram account.
        </p>
      )}

      {/* Loading or Error */}
      {loading ? (
        <p className="text-center text-gray-600 mt-10">Loading posts...</p>
      ) : error ? (
        <p className="text-center text-red-500 mt-10">{error}</p>
      ) : posts.length > 0 ? (
        <>
          {/* Select Post */}
          <div className="mb-10 mt-3">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full shadow-xl py-3 px-4 text-sm tracking-wide rounded-lg text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl flex items-center justify-center gap-1"
            >
              {selectedPost ? "Change Selected Post" : "Select Post"}
              <ArrowDropDownIcon />
            </button>
          </div>

          {/* Post List */}
          {dropdownOpen && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4 mt-5">
              {posts.map((post) =>
                post.media_type === "IMAGE" || post.media_type === "CAROUSEL_ALBUM" ? (
                  <div
                    key={post.id}
                    onClick={() => handleSelect(post)}
                    className="cursor-pointer border rounded hover:shadow transition p-2"
                  >
                    <img
                      src={post.media_url}
                      alt="Instagram"
                      className="w-full h-48 object-cover rounded mb-2"
                    />
                    {post.caption && (
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {post.caption}
                      </p>
                    )}
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Selected Post & Inputs */}
          {selectedPost && (
            <div className="text-center mt-5">
              <img
                src={selectedPost.media_url}
                alt="Selected"
                className="mx-auto max-w-xs rounded shadow mb-4"
              />
              {selectedPost.caption && (
                <p className="text-gray-700 text-sm mb-4 italic px-4">
                  {selectedPost.caption}
                </p>
              )}

              <div className="w-full max-w-2xl mx-auto px-4">
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Customize post title (optional)..."
                  className="w-full h-12 px-4 mb-4 border rounded"
                />
                <input
                  type="text"
                  value={productUrl}
                  onChange={(e) => setProductUrl(e.target.value)}
                  placeholder="Enter product URL..."
                  className="w-full h-12 px-4 mb-4 border rounded"
                />

                {createError && (
                  <p className="text-red-500 mb-4">{createError}</p>
                )}

                <button
                  onClick={handleCreateProductLink}
                  disabled={creatingLink}
                className="w-full shadow-xl py-3 px-4 text-sm tracking-wide rounded-lg text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl flex items-center justify-center gap-1"
                >
                  {creatingLink ? "Creating  post ..." : "Add post"}
                </button>

                {shareableLink && (
                  <div className="mt-6">
                    <p className="text-sm text-gray-600 mb-2">Your Shareable Link:</p>
                    <div className="flex items-center justify-center gap-2">
                      <a
                        href={shareableLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                      >
                        {shareableLink}
                      </a>
                      <button
                        onClick={handleCopyToClipboard}
                        className="p-2 text-white bg-blue-500 hover:bg-blue-600 rounded"
                      >
                        <ShareIcon />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-gray-500 mt-10">No Instagram posts found.</p>
      )}
    </div>
  );
};

export default InstagramProductSelect;
